export const ACHIEVEMENT_BADGE_SIZES_PX = {
	small: 26,
	medium: 48,
	large: 60,
} as const;

export type AchievementBadgeSize = keyof typeof ACHIEVEMENT_BADGE_SIZES_PX;
export type AchievementBadgeVariant = 'light' | 'dark';

export const ACHIEVEMENT_BADGE_COLORS = ['default', 'gold', 'silver', 'bronze', 'purple', 'monochrome'] as const;
export const ACHIEVEMENT_BADGE_SHAPES = ['hexagon', 'square', 'circle', 'shield'] as const;

export type AchievementBadgeColor = typeof ACHIEVEMENT_BADGE_COLORS[number];
export type AchievementBadgeShape = typeof ACHIEVEMENT_BADGE_SHAPES[number];

type BadgeColorTheme = {
	icon: string;
	fill: string;
	stroke: string;
	counter: { color: string; background: string };
};

// Badge art, not UI chrome: these are the badge palette, so they stay hex rather than
// mainTheme tokens. The record type makes every colour × theme pair mandatory.
const LIGHT_COUNTER = { color: '#FFFFFF', background: '#000000' };
const DARK_COUNTER = { color: '#0F1E38', background: '#FFFFFF' };
const DEFAULT_LIGHT_COUNTER = { color: '#FFFFFF', background: '#126BDD' };
const MONOCHROME_DARK_COUNTER = { color: '#FFFFFF', background: '#0F1E38' };

const COLOR_THEMES: Record<AchievementBadgeColor, Record<AchievementBadgeVariant, BadgeColorTheme>> = {
	default: {
		light: { icon: '#FFFFFF', stroke: '#0F1E38', fill: '#2D415A', counter: DEFAULT_LIGHT_COUNTER },
		dark: { icon: '#FFFFFF', stroke: '#B8CCF0', fill: '#566583', counter: DARK_COUNTER },
	},
	gold: {
		light: { icon: '#754605', stroke: '#FAA201', fill: '#FEBD01', counter: LIGHT_COUNTER },
		dark: { icon: '#1A253F', stroke: '#FED767', fill: '#FEBD01', counter: DARK_COUNTER },
	},
	silver: {
		light: { icon: '#1A253F', stroke: '#B5C1D1', fill: '#E1E8F1', counter: LIGHT_COUNTER },
		dark: { icon: '#1A253F', stroke: '#F0F7FF', fill: '#C3CEDD', counter: DARK_COUNTER },
	},
	bronze: {
		light: { icon: '#1A253F', stroke: '#D7702E', fill: '#EF9C58', counter: LIGHT_COUNTER },
		dark: { icon: '#FFFFFF', stroke: '#F49547', fill: '#D6783C', counter: DARK_COUNTER },
	},
	purple: {
		light: { icon: '#FFFFFF', stroke: '#6258DD', fill: '#7F77FF', counter: LIGHT_COUNTER },
		dark: { icon: '#FFFFFF', stroke: '#9992FF', fill: '#7269E0', counter: DARK_COUNTER },
	},
	monochrome: {
		light: { icon: '#FFFFFF', stroke: '#0F1E38', fill: '#0F1E38', counter: DARK_COUNTER },
		dark: { icon: '#0F1E38', stroke: '#FFFFFF', fill: '#FFFFFF', counter: MONOCHROME_DARK_COUNTER },
	},
};

/** Colour and variant arrive as API strings: anything unknown falls back to default / dark. */
export function getAchievementColorTheme(color?: string | null, variant?: string): BadgeColorTheme {
	const byTheme = COLOR_THEMES[(color ?? 'default') as AchievementBadgeColor] ?? COLOR_THEMES.default;
	return byTheme[variant as AchievementBadgeVariant] ?? byTheme.dark;
}
